import React from 'react';
import {
  Box,
  Button,
  Chip,
  Typography,
  Divider,
  Stack,
  Paper,
  Avatar,
  Tooltip,
  IconButton,
  Badge
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Check as AcceptIcon,
  Download as DownloadIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  ForwardToInbox as ForwardIcon,
  Info as AssignedIcon,
  MoreHoriz as InProgressIcon,
  CheckCircle as CompletedIcon,
  Cancel as RejectedIcon,
  Attachment as AttachmentIcon
} from '@mui/icons-material';
import LocalizedDate from '../common/LocalizedDate';
import { getSafeColor } from '../../utils/colorUtils';
import { useTranslation } from 'react-i18next';

const statusIcons = {
  'ASSIGNED': <AssignedIcon fontSize="small" />,
  'ACCEPTED': <AcceptIcon fontSize="small" />,
  'IN_PROGRESS': <InProgressIcon fontSize="small" />,
  'COMPLETED': <CompletedIcon fontSize="small" />,
  'REJECTED': <RejectedIcon fontSize="small" />,
  'FORWARDED': <ForwardIcon fontSize="small" />,
  'REJECTED_RECEIPT': <RejectedIcon fontSize="small" />
};

const CompactTaskRow = ({ 
  task, 
  viewMode, 
  onView, 
  onDelete,
  onDownload,
  t 
}) => {
  const { t: i18nextT, i18n } = useTranslation(['tasks', 'common', 'documents']);
  const isRtl = ['dari','ps'].includes(i18n.language);
  const document = task.document || {};
  const assignee = task.assignee || {};
  const assigner = task.assigner || {};
  
  const effectiveStatus = getEffectiveStatus(task);
  const isPastDue = task.due_date && new Date(task.due_date) < new Date();
  
  const statusColors = {
    'ASSIGNED': 'default',
    'ACCEPTED': 'primary',
    'IN_PROGRESS': 'primary',
    'COMPLETED': 'success',
    'REJECTED': 'error',
    'FORWARDED': 'warning',
    'REJECTED_RECEIPT': 'error'
  };

  const priorityColors = {
    'HIGH': 'error',
    'MEDIUM': 'warning',
    'LOW': 'success'
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (onDownload) {
      onDownload(document.id);
    } else if (document.id) {
      window.open(`${process.env.REACT_APP_API_BASE_URL}/documents/${document.id}/download`, '_blank');
    }
  };

  return (
    <Paper
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1,
        mb: 1,
        transition: 'all 0.2s',
        borderLeft: '3px solid',
        borderLeftColor: document.priority ? 
          `${priorityColors[document.priority]}.main` : 'divider',
        '&:hover': {
          backgroundColor: 'action.hover',
          boxShadow: 1
        }
      }}
      onClick={() => onView(task)}
    >
      {/* Status */}
      <Box sx={{ width: 120, flexShrink: 0 }}>
        <Chip
          label={i18nextT(`tasks:${effectiveStatus}`, effectiveStatus)}
          icon={statusIcons[effectiveStatus]}
          size="small"
          color={statusColors[effectiveStatus] || 'default'}
          sx={{ 
            minWidth: 100,
            '& .MuiChip-icon': { 
              ml: effectiveStatus === 'ASSIGNED' ? 0 : '4px' 
            } 
          }}
        />
      </Box>

      {/* Document reference */}
      <Box sx={{ width: 90, flexShrink: 0 }}>
        <Typography variant="body2" noWrap>
          {document.reference_number || '--'}
        </Typography>
      </Box>

      {/* Document title */}
      <Box sx={{ flexGrow: 1, minWidth: 0, px: 1 }}>
        <Tooltip title={document.title || i18nextT('tasks:no_document')}>
          <Typography noWrap sx={{ fontWeight: 'medium' }}>
            {document.title || i18nextT('tasks:no_document')}
          </Typography>
        </Tooltip>
        {document.description && (
          <Tooltip title={document.description}>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {document.description}
            </Typography>
          </Tooltip>
        )}
      </Box>

{/* Assignee/Assigner */}
<Box sx={{ 
  width: 190, 
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center'
}}>
  <Avatar 
    sx={{ 
      width: 24, 
      height: 24, 
      fontSize: '0.75rem',
      bgcolor: getSafeColor(viewMode === 'assigned-by-me' ? 
        assignee.full_name || assignee.username : 
        assigner.full_name || assigner.username),
      mr: isRtl ? 0 : 1.5,
      ml: isRtl ? 1.5 : 0
    }}
  >
    {viewMode === 'assigned-by-me' ? 
      (assignee.full_name || assignee.username).charAt(0).toUpperCase() :
      (assigner.full_name || assigner.username).charAt(0).toUpperCase()}
  </Avatar>
  <Typography 
    variant="body2" 
    noWrap
    sx={{
      marginLeft: 1, // 8px spacing
      flex: 1,
      minWidth: 0 // ensures text truncation works
    }}
  >
    {viewMode === 'assigned-by-me' ? 
      assignee.full_name || assignee.username :
      assigner.full_name || assigner.username}
  </Typography>
</Box>

      {/* Due Date */}
      <Box sx={{ width: 120, flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TimeIcon fontSize="small" color={isPastDue ? "error" : "action"} />
          <LocalizedDate 
            date={task.due_date} 
            variant="body2"
            color={isPastDue ? "error" : "text.secondary"}
          />
        </Stack>
      </Box>

      {/* Actions */}
      <Box sx={{ width: 80, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
        <Stack direction="row" spacing={0.5}>
          {document.attachments_count > 0 && (
            <Tooltip title={i18nextT('tasks:attachments_count', { count: document.attachments_count })}>
              <IconButton size="small" onClick={handleDownload}>
                <Badge badgeContent={document.attachments_count} color="primary" max={9}>
                  <AttachmentIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title={i18nextT('common:view')}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(task); }}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {onDelete && task.status === 'ASSIGNED' && (
            <Tooltip title={i18nextT('common:delete')}>
              <IconButton 
                size="small" 
                color="error"
                onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Paper>
  );
};

function getEffectiveStatus(task) {
  if (!task) return '';
  if (task.effective_status) return task.effective_status;
  if (task.status === 'FORWARDED' && task.metadata?.forwardedCompletion) {
    return task.metadata.forwardedCompletion.status || task.status;
  }
  return task.status;
}

export default CompactTaskRow;